/**
 * CentrosCustoPage Component
 * 
 * Página de listagem e gerenciamento de centros de custo.
 * Permite visualizar, criar, editar e excluir centros de custo.
 * 
 * Funcionalidades:
 * - Listagem hierárquica de centros de custo
 * - Filtros por status
 * - Modal de criação/edição
 * - Suporte a hierarquia (centros e subcentros)
 */

import { useState, useEffect, useCallback } from 'react'
import { Edit, Trash2, Plus, Search } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { costCenterService } from '../../../../services/erpApi'
import './CentrosCustoPage.css'

function CentrosCustoPage() {
  const [centros, setCentros] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCentro, setEditingCentro] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    parentId: null,
    active: true,
    description: ''
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    active: ''
  })

  const loadCentros = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (filters.search.trim()) {
        params.search = filters.search.trim()
      }
      if (filters.active !== '') {
        params.active = filters.active
      }

      const response = await costCenterService.getAll(params)
      if (response.success) {
        setCentros(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar centros de custo:', error)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadCentros()
  }, [loadCentros])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        name: formData.name,
        parentId: formData.parentId || null,
        active: formData.active,
        description: formData.description || null
      }

      let response
      if (editingCentro) {
        response = await costCenterService.update(editingCentro.id, dataToSend)
      } else {
        response = await costCenterService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadCentros()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar centro de custo' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar centro de custo:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar centro de custo'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (centro) => {
    setEditingCentro(centro)
    setFormData({
      name: centro.name || '',
      parentId: centro.parent_id || null,
      active: centro.active !== undefined ? centro.active : true,
      description: centro.description || ''
    })
    setModalOpen(true)
  }

  const handleDelete = async (centro) => {
    const confirmed = window.confirm(
      `Deseja excluir o centro de custo "${centro.name}"?\n\n` +
      'Lançamentos vinculados (contas a pagar/receber e fluxo de caixa) permanecerão no sistema, mas sem centro de custo.'
    )

    if (!confirmed) return

    try {
      const response = await costCenterService.delete(centro.id)
      if (response.success) {
        if (response.message) {
          alert(response.message)
        }
        loadCentros()
      } else {
        alert(response.message || 'Erro ao excluir centro de custo')
      }
    } catch (error) {
      console.error('Erro ao excluir centro de custo:', error)
      alert(error.response?.data?.message || 'Erro ao excluir centro de custo')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      parentId: null,
      active: true,
      description: ''
    })
    setEditingCentro(null)
    setErrors({})
  }

  const getParentCentros = () => {
    return centros.filter(c => !c.parent_id && c.id !== editingCentro?.id)
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    {
      key: 'parent',
      label: 'Centro Pai',
      render: (value, row) => {
        if (row.parent_id) {
          const parent = centros.find(c => c.id === row.parent_id)
          return parent ? parent.name : '-'
        }
        return '-'
      }
    },
    {
      key: 'active',
      label: 'Status',
      render: (value) => {
        return value ? (
          <span className="status-badge active">Ativo</span>
        ) : (
          <span className="status-badge inactive">Inativo</span>
        )
      }
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <div className="centros-custo-page">
      <div className="page-header">
        <div>
          <h1>Centros de Custo</h1>
          <p>Gerencie os centros de custo</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm()
            setModalOpen(true)
          }}
        >
          <Plus size={18} />
          Novo Centro
        </button>
      </div>

      <div className="page-filters">
        <div className="filters-row">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar centros de custo..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="search-input"
            />
          </div>
          <select
            value={filters.active}
            onChange={(e) => setFilters({ ...filters, active: e.target.value })}
            className="filter-select"
          >
            <option value="">Todos os status</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={centros}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhum centro de custo encontrado"
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingCentro ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingCentro ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
          />

          <FormField
            label="Centro Pai"
            name="parentId"
            type="select"
            value={formData.parentId || ''}
            onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
            error={errors.parentId}
            options={[
              { value: '', label: 'Nenhum (centro principal)' },
              ...getParentCentros().map(c => ({ value: c.id, label: c.name }))
            ]}
          />

          <FormField
            label="Descrição"
            name="description"
            type="textarea"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={errors.description}
          />

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            error={errors.active}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
    </div>
  )
}

export default CentrosCustoPage

